import {
  CreateButton,
  DateField,
  DeleteButton,
  FilterDropdown,
  List,
  SaveButton,
  TextField,
  getDefaultSortOrder,
  useTable,
  useSelect,
  UrlField,
} from "@refinedev/antd";

// It is recommended to use explicit import as seen below to reduce bundle size.
// import { IconName } from "@ant-design/icons";
import * as Icons from "@ant-design/icons";

import {
  Button,
  Col,
  Divider,
  Form,
  FormProps,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Alert,
  Timeline,
} from "antd";
import type { ColumnsType } from "antd/es/table";

import {
  CrudFilters,
  IResourceComponentsProps,
  getDefaultFilter,
  useModal,
  useGetIdentity,
  useNavigation,
} from "@refinedev/core";

import { IVesselResource } from "interfaces";
import prettyBytes from "pretty-bytes";
import dayjs from "dayjs";
import isoWeek from 'dayjs/plugin/isoWeek';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React from "react";
import { DownloadResource } from "./components/download-resource";
import { EmailIncrementalUpdate } from "./components/email-incremental-update";
// It is recommended to use explicit import as seen below to reduce bundle size.
// import { IconName } from "@ant-design/icons";
import { DownloadOutlined, FileTextOutlined } from "@ant-design/icons";
export const VesselResourcesList: React.FC<IResourceComponentsProps> = () => {
  const [record, setRecord] = React.useState<IVesselResource>();
  const [showIncrementalUpdates, setShowIncrementalUpdates] = React.useState(false);
  const { data: identity } = useGetIdentity();
  const { create } = useNavigation();  
  const isVesselUser = identity?.role === "VesselUser";

  // extend dayjs with required plugins 
  dayjs.extend(isoWeek);
  dayjs.extend(utc);
  dayjs.extend(timezone);

  // Function to get the display week where Thursday to next Wednesday is considered as the current ISO week
  const getDisplayWeekNumber = () => {
    // Get current date in IST timezone
    const now = dayjs().tz("Asia/Kolkata");
    
    // Find the most recent Thursday (or today if it's Thursday)
    // This is the start of our display week
    let thursdayOfWeek;
    const dayOfWeek = now.day(); // 0 is Sunday, 4 is Thursday
    
    if (dayOfWeek === 4) {
      // Today is Thursday
      thursdayOfWeek = now;
    } else if (dayOfWeek > 4) {
      // Friday (5) or Saturday (6)
      thursdayOfWeek = now.subtract(dayOfWeek - 4, 'day');
    } else {
      // Sunday (0), Monday (1), Tuesday (2), Wednesday (3)
      thursdayOfWeek = now.subtract(dayOfWeek + 3, 'day');
    }
    
    // Get the ISO week of this Thursday
    // This is our display week number
    return thursdayOfWeek.isoWeek();
  };

  const { tableProps, sorters, tableQuery: { isLoading }, searchFormProps, filters } =
    useTable<IVesselResource>({
      resource: "vessel-resource",
      syncWithLocation: false,

      sorters: {
        permanent: [
          {
            field: "sortOrder",
            order: "asc",
          },
        ]
      }
    });
  
  // Filter the data based on showIncrementalUpdates state
  const filteredData = React.useMemo(() => {
    if (!tableProps.dataSource) return [];
    
    return tableProps.dataSource.filter((item: IVesselResource) => {
      if (showIncrementalUpdates) {
        // Show only AVCS Permits and AVCS Incremental Update when button is clicked
        return ['AVCS Permits', 'AVCS Weekly Update', 'AIO Weekly Update', 'AVCS Last Order Update'].includes(item.title);
      } else {
        // Show default set of records
        return ['AVCS Permits', 'AVCS Full Exchange Set', 'AIO (Cumulative)', 'eMariner Portal User Guide'].includes(item.title);
      }
    });
  }, [tableProps.dataSource, showIncrementalUpdates]);

  // Create new table props with filtered data
  const modifiedTableProps = {
    ...tableProps,
    dataSource: filteredData,
  };

  // Check if IMO Number filter is applied
  const isImoFilterApplied = filters?.some(
    (filter) => 'field' in filter && filter.field === "imoNumber" && filter.value
  );

  const { show, visible, close } = useModal();

  const columns: ColumnsType<IVesselResource> = [
    {
      title: "IMO Number",
      dataIndex: "imoNumber",
      key: "imoNumber",
      width: 150,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            placeholder="Select IMO Number"
            {...imoSelectProps}
          />
        </FilterDropdown>
      ),
      defaultFilteredValue: getDefaultFilter("imoNumber", filters),
    },
    {
      title: "Vessel Name",
      dataIndex: "vesselName",
      key: "vesselName",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            placeholder="Select Vessel Name"
            {...vesselNameSelectProps}
          />
        </FilterDropdown>
      ),
      defaultFilteredValue: getDefaultFilter("vesselName", filters),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("title", sorters),
      sorter: true,
    },
    {
      title: "File Name",
      dataIndex: "fileName",
      key: "fileName",
      width: 300,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("fileName", sorters),
      sorter: true,
    },
    {
      title: "File Size",
      dataIndex: "fileSize",
      key: "fileSize",
      width: 120,
      className: "wrap-text",
      render: (value) => <TextField value={value ? prettyBytes(value) : null} />,
      defaultSortOrder: getDefaultSortOrder("fileSize", sorters),
      sorter: true,
    },
    {
      title: "Last Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 200,
      className: "wrap-text",
      render: (value) => (
        <DateField format="DD-MMM-YYYY hh:mm:ss A" value={value} />
      ),
      defaultSortOrder: getDefaultSortOrder("updatedAt", sorters),
      sorter: (a: IVesselResource, b: IVesselResource) => {
        return dayjs(a.updatedAt).unix() - dayjs(b.updatedAt).unix();
      },
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space>
          <EmailIncrementalUpdate
            imoNumber={record?.imoNumber}
            disabled={record?.title !== 'AVCS Weekly Update'}
          />
          <DownloadResource
            storageUrl={record?.storageUrl}
            imoNumber={record?.imoNumber}
            ukhoLicenceId={record?.ukhoLicenceId}
            fileName={record?.fileName}
          />
          <DeleteButton
            hideText
            size="small"
            recordItemId={record.id}
            disabled
          />
        </Space>
      ),
    },
  ];

  const { selectProps: imoSelectProps } = useSelect<IVesselResource>({
    resource: "vessel-resource",
    optionLabel: "imoNumber",
    optionValue: "imoNumber",

    pagination: {
      mode: "server"
    }
  });

  const { selectProps: vesselNameSelectProps } = useSelect<IVesselResource>({
    resource: "vessel-resource",
    optionLabel: "vesselName",
    optionValue: "vesselName",

    pagination: {
      mode: "server"
    }
  });

  return (
    <>
      <List
        breadcrumb={false}
        headerProps={{
          // onBack: () => window.history.back(),
          title: "Permits & Data",
          subTitle: "avcs permits, avcs exchange set",
          tags: isLoading ? (
            <Space>
              <Tag color="orange">loading</Tag>
              <Tag color="gold">
                Update Week {getDisplayWeekNumber()}
              </Tag>
            </Space>
          ) : (
            <Space>
              <Tag color="green">live</Tag>
              <Tag color="gold">
                Update Week {getDisplayWeekNumber()}
              </Tag>
            </Space>
          ),
          extra: (
            <React.Fragment>
              <Space>
                <Button
                  onClick={() => setShowIncrementalUpdates(!showIncrementalUpdates)}
                  disabled={!isVesselUser && !isImoFilterApplied}
                >
                  AVCS Incremental Updates
                </Button>
                <CreateButton
                  children="Refresh Data"
                  disabled={false}
                />
                <Button
                  icon={<FileTextOutlined />}
                  onClick={() => create("vessel-resource-holdings")}
                >
                  Holdings Report
                </Button>
              </Space>
            </React.Fragment>
          ),
          ghost: true,
          footer: (
            <Divider plain>
              © {new Date().getFullYear()} e-Mariner NavSolutions
            </Divider>
          ),
        }}
      >
        <Row gutter={[16, 16]} align="stretch">
          <Col xl={24} xs={24} style={{ display: 'flex' }}>
            <Alert
              type="info"
              message={
                <Typography.Title level={5} style={{ color: 'inherit', margin: '0', padding: '0' }}>
                  How to use Permits & Data:
                </Typography.Title>
              }
              description={
                <div style={{ padding: '10px 0' }}>
                  <Timeline style={{ paddingBottom: 0 }}>
                    <Timeline.Item style={{ paddingTop: 9 }}>
                      <Typography.Text strong>Step 1</Typography.Text>{" "}
                      Download AVCS Permit
                    </Timeline.Item>
                    <Timeline.Item>
                      <Typography.Text strong>Step 2</Typography.Text>{" "}
                      Download AVCS Exchange Set
                    </Timeline.Item>
                    <Timeline.Item>
                      <Typography.Text strong>Step 3</Typography.Text>{" "}
                      Download AIO Exchange Set
                    </Timeline.Item>
                    <Timeline.Item>
                      <Typography.Text strong>Step 4</Typography.Text>{" "}
                      Apply these files to your ECDIS
                    </Timeline.Item>
                    <Timeline.Item style={{ paddingBottom: 0 }}>
                      <Typography.Text strong>Step 5</Typography.Text>{" "}
                      If you are not able to download the AVCS Exchange Set or the AIO Exchange Set 
                      from Step 2 and Step 3 (due to the file size), please click
                      <Button type="link" onClick={() => setShowIncrementalUpdates(!showIncrementalUpdates)}>
                        <DownloadOutlined /> AVCS Incremental Updates
                      </Button>{" "}
                      Please note that it is always better to use the exchange set in Step 2 and Step 3 above
                    </Timeline.Item>
                  </Timeline>
                </div>
              }
              showIcon
              style={{
                marginBottom: '9px',
                paddingBottom: '0px',
                flex: 1,
              }}
            />
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col xl={24} xs={24}>
            <Table
              {...modifiedTableProps}
              columns={columns}
              rowKey="id"
              size="middle"
              scroll={{ x: 1200 }}
              sticky
              pagination={isVesselUser ? false : {
                ...tableProps.pagination,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              }}
            />
          </Col>
        </Row>
      </List>
    </>
  );
};